import { useRef, useState } from "react";
import { PaperPlaneRight, CircleNotch, X, ArrowsClockwise, Paperclip, ChatCircleText } from "@phosphor-icons/react";
import { toast } from "@/components/ui/sonner";
import api from "@/lib/api";
import MicButton from "@/components/MicButton";

const SHORTCUTS = [
  { label: "Correct the record", hint: "Correction: " },
  { label: "Add information", hint: "Additional information: " },
  { label: "Challenge an assumption", hint: "I want to challenge this assumption: " },
  { label: "Reframe the problem", hint: "I think a better framing is: " },
];

// Bottom-docked console for The Problem tab: respond, reframe/revise, or attach evidence.
export default function ProblemConsole({ sessionId, onChanged }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState("");
  const fileRef = useRef(null);
  const taRef = useRef(null);

  const applyShortcut = (hint) => { setText((t) => (t ? t + "\n" + hint : hint)); taRef.current?.focus(); };

  const attachAndSend = async () => {
    setBusy("evidence");
    try {
      let fileId = null, desc = text.trim();
      if (file) { const up = await api.uploadFile(file); fileId = up.file_id; if (!desc) desc = `Uploaded document: ${up.filename}`; }
      await api.addEvidence(sessionId, desc, fileId);
      setText(""); setFile(null); if (fileRef.current) fileRef.current.value = "";
      toast.success("Evidence evaluated", { description: "Assessed for how it bears on the analysis." });
      onChanged?.();
    } catch (e) { toast.error("Could not add evidence", { description: e?.response?.data?.detail || e.message }); }
    finally { setBusy(""); }
  };

  const revise = async () => {
    setBusy("revise");
    try { await api.revise(sessionId, text.trim()); setText(""); toast.success("Updating the analysis", { description: "An updated version will appear shortly." }); onChanged?.(); }
    catch (e) { toast.error("Update failed", { description: e?.response?.data?.detail?.message || e?.response?.data?.detail || e.message }); }
    finally { setBusy(""); }
  };

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center px-4 pb-12">
      <div className="pointer-events-auto w-full max-w-2xl">
        {open ? (
          <div className="border border-foreground/20 bg-card/95 p-3 shadow-xl backdrop-blur">
            <div className="mb-2 flex items-center justify-between">
              <span className="overline">Correct, add to, or reframe the analysis</span>
              <button data-testid="problem-console-close" onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground"><X size={16} /></button>
            </div>
            <div className="relative">
              <textarea
                ref={taRef}
                data-testid="problem-console-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={3}
                placeholder="Correct, challenge, expand, or reframe any part of the analysis — or describe evidence to attach…"
                className="w-full resize-y rounded-sm border border-input bg-background p-3 pr-11 text-sm leading-relaxed outline-none transition-colors focus:border-foreground focus:ring-1 focus:ring-ring"
              />
              <MicButton size={17} className="absolute right-2.5 top-2.5" onResult={(t) => setText((p) => (p ? p.trim() + " " + t : t))} />
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {SHORTCUTS.map((s) => (
                <button key={s.label} onClick={() => applyShortcut(s.hint)} className="rounded-sm border border-border px-2.5 py-1 text-[11px] text-muted-foreground transition-colors hover:border-foreground/40 hover:text-foreground">{s.label}</button>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <input ref={fileRef} type="file" accept=".pdf,.docx,.txt,.md,.csv" onChange={(e) => setFile(e.target.files?.[0] || null)} className="hidden" />
              <button onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-1.5 rounded-sm border border-border px-2.5 py-2 text-xs text-muted-foreground transition-colors hover:border-foreground/40 hover:text-foreground"><Paperclip size={13} /> Attach</button>
              {file && <span className="inline-flex items-center gap-1.5 rounded-sm bg-muted px-2 py-1 text-[11px]">{file.name}<button onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }}><X size={12} /></button></span>}
              <div className="flex-1" />
              {file ? (
                <button data-testid="problem-console-evidence-btn" onClick={attachAndSend} disabled={!!busy} className="inline-flex items-center gap-1.5 rounded-sm bg-primary px-3 py-2 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/85 disabled:opacity-40">{busy === "evidence" ? <CircleNotch size={14} className="animate-spin" /> : <Paperclip size={14} />} Add evidence</button>
              ) : (
                <button data-testid="problem-console-revise-btn" onClick={revise} disabled={!!busy || !text.trim()} className="inline-flex items-center gap-1.5 rounded-sm bg-primary px-3 py-2 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/85 disabled:opacity-40">{busy === "revise" ? <CircleNotch size={14} className="animate-spin" /> : <ArrowsClockwise size={14} />} Update the analysis</button>
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1.5">
            <span className="rounded-full bg-background/80 px-2.5 py-0.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground shadow-sm backdrop-blur">Your turn — steer the analysis</span>
            <button data-testid="problem-console-open" onClick={() => setOpen(true)}
              className="group mx-auto flex items-center gap-2.5 rounded-full px-6 py-3 text-[15px] font-semibold text-white shadow-xl ring-1 ring-black/10 transition-transform hover:scale-[1.03]"
              style={{ background: "hsl(var(--tag-preference))" }}>
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white/70" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-white" />
              </span>
              <ChatCircleText size={19} weight="fill" /> Respond, reframe, or add evidence
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
